/**
 * DevExtreme (esm/__internal/grids/grid_core/column_headers/m_column_headers.js)
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import eventsEngine from "../../../../common/core/events/core/events_engine";
import messageLocalization from "../../../../common/core/localization/message";
import domAdapter from "../../../../core/dom_adapter";
import $ from "../../../../core/renderer";
import { Deferred } from "../../../../core/utils/deferred";
import { extend } from "../../../../core/utils/extend";
import { each } from "../../../../core/utils/iterator";
import { getHeight } from "../../../../core/utils/size";
import { isDefined } from "../../../../core/utils/type";
import { ColumnContextMenuMixin } from "../../../grids/grid_core/context_menu/m_column_context_menu_mixin";
import { CLASSES as REORDERING_CLASSES } from "../columns_resizing_reordering/const";
import { registerKeyboardAction } from "../m_accessibility";
import { ColumnsView } from "../views/m_columns_view";
const CELL_CONTENT_CLASS = "text-content";
const HEADERS_CLASS = "headers";
const NOWRAP_CLASS = "nowrap";
const ROW_CLASS_SELECTOR = ".dx-row";
const HEADER_ROW_CLASS = "dx-header-row";
const COLUMN_LINES_CLASS = "dx-column-lines";
const CONTEXT_MENU_SORT_ASC_ICON = "context-menu-sort-asc";
const CONTEXT_MENU_SORT_DESC_ICON = "context-menu-sort-desc";
const CONTEXT_MENU_SORT_NONE_ICON = "context-menu-sort-none";
const CELL_FOCUS_DISABLED_CLASS = "dx-cell-focus-disabled";
const VISIBILITY_HIDDEN_CLASS = "dx-visibility-hidden";
const TEXT_CONTENT_ALIGNMENT_CLASS_PREFIX = "dx-text-content-alignment-";
const SORT_INDICATOR_CLASS = "dx-sort-indicator";
const SORT_INDEX_INDICATOR_CLASS = "dx-sort-index-indicator";
const HEADER_FILTER_CLASS_SELECTOR = ".dx-header-filter";
const HEADER_FILTER_INDICATOR_CLASS = "dx-header-filter-indicator";
const MULTI_ROW_HEADER_CLASS = "dx-header-multi-row";
const LINK = "dx-link";
const createCellContent = function (that, $cell, options) {
  const $cellContent = $("<div>").addClass(that.addWidgetPrefix("text-content"));
  that.setAria("role", "presentation", $cellContent);
  addCssClassesToCellContent(that, $cell, options.column, $cellContent);
  const showColumnLines = that.option("showColumnLines");
  const contentAlignment = that.getController("columns").getHeaderContentAlignment(options.column.alignment);
  return $cellContent[showColumnLines || "right" === contentAlignment ? "appendTo" : "prependTo"]($cell);
};
function addCssClassesToCellContent(that, $cell, column, $cellContent) {
  const $indicatorElements = that._getIndicatorElements($cell, true);
  const $visibleIndicatorElements = that._getIndicatorElements($cell);
  const indicatorCount = null === $indicatorElements || void 0 === $indicatorElements ? void 0 : $indicatorElements.length;
  const columnAlignment = that._getColumnAlignment(column.alignment);
  const sortIndicatorClassName = `.${that._getIndicatorClassName("sort")}`;
  const sortIndexIndicatorClassName = `.${that._getIndicatorClassName("sortIndex")}`;
  const $sortIndicator = $visibleIndicatorElements.filter(sortIndicatorClassName);
  const $sortIndexIndicator = $visibleIndicatorElements.children().filter(sortIndexIndicatorClassName);
  $cellContent = $cellContent || $cell.children(`.${that.addWidgetPrefix("text-content")}`);
  $cellContent.toggleClass("dx-text-content-alignment-" + columnAlignment, indicatorCount > 0).toggleClass("dx-text-content-alignment-" + ("left" === columnAlignment ? "right" : "left"), indicatorCount > 0 && "center" === column.alignment).toggleClass("dx-sort-indicator", !!$sortIndicator.length).toggleClass("dx-sort-index-indicator", !!$sortIndexIndicator.length).toggleClass("dx-header-filter-indicator", !!$visibleIndicatorElements.filter(`.${that._getIndicatorClassName("headerFilter")}`).length);
}
export class ColumnHeadersView extends ColumnContextMenuMixin(ColumnsView) {
  init() {
    super.init();
    this._headerPanelView = this.getView("headerPanel");
    this._headerFilterController = this.getController("headerFilter");
    this._dataController = this.getController("data");
    this._headersKeyboardNavigation = this.getController("headersKeyboardNavigation");
  }
  _createTable() {
    const $table = super._createTable.apply(this, arguments);
    eventsEngine.on($table, "mousedown selectstart", this.createAction(e => {
      const {
        event: event
      } = e;
      if (event.shiftKey) {
        event.preventDefault();
      }
    }));
    return $table;
  }
  _isLegacyKeyboardNavigation() {
    return this.option("useLegacyKeyboardNavigation");
  }
  _getDefaultTemplate(column) {
    const that = this;
    return function ($container, options) {
      const {
        caption: caption
      } = column;
      const needCellContent = !column.command || caption && "expand" !== column.command;
      if ("empty" === column.command) {
        that._renderEmptyMessage($container, options);
      } else if (needCellContent) {
        const $content = createCellContent(that, $container, options);
        $content.text(caption);
      } else if (column.command) {
        $container.html("&nbsp;");
      }
    };
  }
  _renderEmptyMessage($container, options) {
    const textEmpty = this._getEmptyHeaderText();
    if (!textEmpty) {
      $container.html("&nbsp;");
      return;
    }
    const $cellContent = createCellContent(this, $container, options);
    const needSplit = textEmpty.includes("{0}");
    if (needSplit) {
      const [leftPart, rightPart] = textEmpty.split("{0}");
      const columnChooserTitle = messageLocalization.format("dxDataGrid-emptyHeaderColumnChooserText");
      const columnChooserView = this._columnChooserView;
      const $link = $("<a>").text(columnChooserTitle).addClass(LINK);
      eventsEngine.on($link, "click", this.createAction(() => columnChooserView.showColumnChooser()));
      $cellContent.append(domAdapter.createTextNode(leftPart)).append($link).append(domAdapter.createTextNode(rightPart));
    } else {
      $cellContent.text(textEmpty);
    }
  }
  _getEmptyHeaderText() {
    const hasHiddenColumns = !!this._columnChooserView.hasHiddenColumns();
    const hasGroupedColumns = !!this._headerPanelView.hasGroupedColumns();
    switch (true) {
      case hasHiddenColumns && hasGroupedColumns:
        return messageLocalization.format("dxDataGrid-emptyHeaderWithColumnChooserAndGroupPanelText");
      case hasGroupedColumns:
        return messageLocalization.format("dxDataGrid-emptyHeaderWithGroupPanelText");
      case hasHiddenColumns:
        return messageLocalization.format("dxDataGrid-emptyHeaderWithColumnChooserText");
      default:
        return "";
    }
  }
  _getHeaderTemplate(column) {
    return column.headerCellTemplate || {
      allowRenderToDetachedContainer: true,
      render: this._getDefaultTemplate(column)
    };
  }
  _processTemplate(template, options) {
    const that = this;
    let resultTemplate;
    const {
      column: column
    } = options;
    const renderingTemplate = super._processTemplate(template);
    if ("header" === options.rowType && renderingTemplate && column.headerCellTemplate && !column.command) {
      resultTemplate = {
        render(options) {
          const $content = createCellContent(that, options.container, options.model);
          renderingTemplate.render(extend({}, options, {
            container: $content
          }));
        }
      };
    } else {
      resultTemplate = renderingTemplate;
    }
    return resultTemplate;
  }
  _handleDataChanged(e) {
    if ("refresh" !== e.changeType) {
      return;
    }
    if (this._isGroupingChanged || this._requireReady) {
      this._isGroupingChanged = false;
      this.render();
    }
  }
  _renderCell($row, options) {
    const $cell = super._renderCell($row, options);
    if ("header" === options.row.rowType) {
      $cell.addClass("dx-cell-focus-disabled");
      if (!this._isLegacyKeyboardNavigation()) {
        const {
          column: column
        } = options;
        const isCustomCommandColumn = this._columnsController.isCustomCommandColumn(column);
        if (column && (!column.type || isCustomCommandColumn)) {
          $cell.attr("tabindex", this.option("tabindex") || 0);
        }
      }
    }
    return $cell;
  }
  _setCellAriaAttributes($cell, cellOptions, options) {
    super._setCellAriaAttributes($cell, cellOptions, options);
    if ("header" === cellOptions.rowType) {
      if (!cellOptions.column.type) {
        this.setAria("role", "columnheader", $cell);
      }
      if (cellOptions.column && !cellOptions.column.command && !cellOptions.column.isBand) {
        $cell.attr("id", cellOptions.column.headerId);
        this.setAria("label", `${messageLocalization.format("dxDataGrid-ariaColumn")} ${cellOptions.column.caption}`, $cell);
      }
    }
  }
  _createRow(row) {
    const $row = super._createRow.apply(this, arguments);
    $row.toggleClass("dx-column-lines", this.option("showColumnLines"));
    if ("header" === row.rowType) {
      $row.addClass("dx-header-row");
      if (!this._isLegacyKeyboardNavigation()) {
        registerKeyboardAction("columnHeaders", this, $row, "td", this._handleActionKeyDown.bind(this));
      }
    }
    return $row;
  }
  _processHeaderAction(event, $row) {}
  _handleActionKeyDown(args) {
    const {
      event: event
    } = args;
    const $target = $(event.target);
    this._lastActionElement = event.target;
    if ($target.is(".dx-header-filter")) {
      const headerFilterController = this._headerFilterController;
      const $column = $target.closest("td");
      const columnIndex = this.getColumnIndexByElement($column);
      if (columnIndex >= 0) {
        headerFilterController.showHeaderFilterMenu(columnIndex, false);
      }
    } else {
      const $row = $target.closest(".dx-row");
      this._processHeaderAction(event, $row);
    }
    event.preventDefault();
  }
  _renderCore() {
    const $container = this.element();
    const change = {};
    if (this._tableElement && !this._dataController.isLoaded() && !this._hasRowElements) {
      return new Deferred().resolve();
    }
    $container.addClass(this.addWidgetPrefix("headers")).toggleClass(this.addWidgetPrefix("nowrap"), !this.option("wordWrapEnabled"));
    this.setAria("role", "presentation", $container);
    const deferred = this._updateContent(this._renderTable({
      change: change
    }), change);
    $container.toggleClass("dx-header-multi-row", this.getRowCount() > 1);
    super._renderCore.apply(this, arguments);
    return deferred;
  }
  _renderRows() {
    const that = this;
    if (that._dataController.isLoaded() || that._hasRowElements) {
      super._renderRows.apply(that, arguments);
      that._hasRowElements = true;
    }
  }
  _renderRow($table, options) {
    const rowIndex = 1 === this.getRowCount() ? null : options.row.rowIndex;
    options.columns = this.getColumns(rowIndex);
    super._renderRow($table, options);
  }
  _createCell(options) {
    const {
      column: column
    } = options;
    const $cellElement = super._createCell.apply(this, arguments);
    column.rowspan > 1 && "header" === options.rowType && $cellElement.attr("rowSpan", column.rowspan);
    return $cellElement;
  }
  _getRows() {
    const result = [];
    const rowCount = this.getRowCount();
    if (this.option("showColumnHeaders")) {
      for (let i = 0; i < rowCount; i++) {
        result.push({
          rowType: "header",
          rowIndex: i
        });
      }
    }
    return result;
  }
  _getCellTemplate(options) {
    if ("header" === options.rowType) {
      return this._getHeaderTemplate(options.column);
    }
  }
  _columnOptionChanged(e) {
    const {
      changeTypes: changeTypes
    } = e;
    const {
      optionNames: optionNames
    } = e;
    if (changeTypes.grouping || changeTypes.groupExpanding) {
      if (changeTypes.grouping) {
        this._isGroupingChanged = true;
      }
      return;
    }
    super._columnOptionChanged(e);
    if (optionNames.width || optionNames.visible) {
      this.resizeCompleted.fire();
    }
  }
  _isElementVisible(elementOptions) {
    return null === elementOptions || void 0 === elementOptions ? void 0 : elementOptions.visible;
  }
  _alignCaptionByCenter($cell) {
    var _$indicatorsContainer;
    let $indicatorsContainer = this._getIndicatorContainer($cell, true);
    if (null !== (_$indicatorsContainer = $indicatorsContainer) && void 0 !== _$indicatorsContainer && _$indicatorsContainer.length) {
      $indicatorsContainer.filter(".dx-visibility-hidden").remove();
      $indicatorsContainer = this._getIndicatorContainer($cell);
      $indicatorsContainer.clone().addClass("dx-visibility-hidden").css("float", "").insertBefore($cell.children(`.${this.addWidgetPrefix("text-content")}`));
    }
  }
  _updateCell($cell, options) {
    if ("header" === options.rowType && "center" === options.column.alignment) {
      this._alignCaptionByCenter($cell);
    }
    super._updateCell.apply(this, arguments);
  }
  _updateIndicator($cell, column, indicatorName) {
    const $indicatorElement = super._updateIndicator.apply(this, arguments);
    if ("center" === column.alignment) {
      this._alignCaptionByCenter($cell);
    }
    addCssClassesToCellContent(this, $cell, column);
    return $indicatorElement;
  }
  _getIndicatorContainer($cell, returnAll) {
    const $indicatorsContainer = super._getIndicatorContainer($cell);
    return returnAll ? $indicatorsContainer : $indicatorsContainer.filter(":not(.dx-visibility-hidden)");
  }
  _isSortableElement($target) {
    return true;
  }
  getHeadersRowHeight() {
    const $tableElement = this.getTableElement();
    const $headerRows = null === $tableElement || void 0 === $tableElement ? void 0 : $tableElement.find(".dx-header-row");
    return (null === $headerRows || void 0 === $headerRows ? void 0 : $headerRows.toArray().reduce((sum, headerRow) => sum + getHeight(headerRow), 0)) || 0;
  }
  getHeaderElement(index) {
    const $columnElements = this.getColumnElements();
    return (null === $columnElements || void 0 === $columnElements ? void 0 : $columnElements.eq(index)) ?? $("");
  }
  getColumnElements(index, bandColumnIndex) {
    const that = this;
    let $cellElement;
    const columnsController = that._columnsController;
    const rowCount = that.getRowCount();
    if (that.option("showColumnHeaders")) {
      if (rowCount > 1 && (!isDefined(index) || isDefined(bandColumnIndex))) {
        const result = [];
        const visibleColumns = isDefined(bandColumnIndex) ? columnsController.getChildrenByBandColumn(bandColumnIndex, true) : columnsController.getVisibleColumns();
        each(visibleColumns, (_, column) => {
          const rowIndex = isDefined(index) ? index : columnsController.getRowIndex(column.index);
          $cellElement = that._getCellElement(rowIndex, columnsController.getVisibleIndex(column.index, rowIndex));
          $cellElement && result.push($cellElement.get(0));
        });
        return $(result);
      }
      if (!index || index < rowCount) {
        return that.getCellElements(index || 0);
      }
    }
    return;
  }
  getColumnIndexByElement($cell) {
    const cellIndex = this.getCellIndex($cell);
    const $row = $cell.closest(".dx-row");
    const {
      rowIndex: rowIndex
    } = $row[0];
    const column = this.getColumns(rowIndex)[cellIndex];
    return column ? column.index : -1;
  }
  getVisibleColumnIndex(columnIndex, rowIndex) {
    const column = this.getColumns()[columnIndex];
    return column ? this._columnsController.getVisibleIndex(column.index, rowIndex) : -1;
  }
  getColumnWidths($tableElement, rowIndex) {
    const $columnElements = this.getColumnElements(rowIndex);
    if (null !== $columnElements && void 0 !== $columnElements && $columnElements.length) {
      return this._getWidths($columnElements);
    }
    return super.getColumnWidths.apply(this, arguments);
  }
  isColumnReorderingEnabled(column) {
    return column.allowReordering && (this.option("allowColumnReordering") ?? this._columnsController.isColumnOptionUsed("allowReordering"));
  }
  allowDragging(column) {
    const rowIndex = column && this._columnsController.getRowIndex(column.index);
    const columns = this.getColumns(rowIndex);
    return this.isColumnReorderingEnabled(column) && columns.length > 1;
  }
  getBoundingRect() {
    const that = this;
    const $columnElements = that.getColumnElements();
    if (null !== $columnElements && void 0 !== $columnElements && $columnElements.length) {
      const offset = that.getTableElement().offset();
      return {
        top: offset.top
      };
    }
    return null;
  }
  getName() {
    return "headers";
  }
  getColumnCount() {
    const $columnElements = this.getColumnElements();
    return $columnElements ? $columnElements.length : 0;
  }
  isVisible() {
    return this.option("showColumnHeaders");
  }
  optionChanged(args) {
    const that = this;
    switch (args.name) {
      case "showColumnHeaders":
      case "wordWrapEnabled":
      case "showColumnLines":
        that._invalidate(true, true);
        args.handled = true;
        break;
      default:
        super.optionChanged(args);
    }
  }
  getHeight() {
    return this.getElementHeight();
  }
  getContextMenuItems(options) {
    let items;
    const {
      column: column
    } = options;
    if (options.row && ("header" === options.row.rowType || "detailAdaptive" === options.row.rowType)) {
      const sortingOptions = this.option("sorting");
      if (sortingOptions && "none" !== sortingOptions.mode && null !== column && void 0 !== column && column.allowSorting) {
        const onItemClick = params => {
          setTimeout(() => {
            this._columnsController.changeSortOrder(column.index, params.itemData.value);
          });
        };
        items = [{
          text: sortingOptions.ascendingText,
          value: "asc",
          disabled: "asc" === column.sortOrder,
          icon: "context-menu-sort-asc",
          onItemClick: onItemClick
        }, {
          text: sortingOptions.descendingText,
          value: "desc",
          disabled: "desc" === column.sortOrder,
          icon: "context-menu-sort-desc",
          onItemClick: onItemClick
        }, {
          name: "clearSorting",
          text: sortingOptions.clearText,
          value: "none",
          disabled: !column.sortOrder,
          icon: "context-menu-sort-none",
          onItemClick: onItemClick
        }];
      }
      if ("header" === options.row.rowType) {
        const moveColumnItems = this.getMoveColumnContextMenuItems(options);
        if (null !== moveColumnItems && void 0 !== moveColumnItems && moveColumnItems.length) {
          items = items ?? [];
          items.push(...moveColumnItems);
        }
      }
    }
    return items;
  }
  getRowCount() {
    var _this$_columnsControl;
    return null === (_this$_columnsControl = this._columnsController) || void 0 === _this$_columnsControl ? void 0 : _this$_columnsControl.getRowCount();
  }
  toggleDraggableColumnClass(columnIndex, value, rowIndex) {
    let i;
    let columnElements;
    const rowCount = this.getRowCount();
    const columns = this._columnsController.getColumns();
    const column = null === columns || void 0 === columns ? void 0 : columns[columnIndex];
    const columnID = (null === column || void 0 === column ? void 0 : column.isBand) && column.index;
    const setColumnClass = (column, index) => {
      if (column.ownerBand === columnID) {
        columnElements.eq(index).toggleClass(this.addWidgetPrefix(REORDERING_CLASSES.draggableColumn), value);
        if (column.isBand) {
          this.toggleDraggableColumnClass(column.index, value, i + 1);
        }
      }
    };
    if (isDefined(columnID)) {
      rowIndex = rowIndex || 0;
      for (i = rowIndex; i < rowCount; i++) {
        columnElements = this.getCellElements(i);
        if (columnElements) {
          const rowColumns = this.getColumns(i);
          rowColumns.forEach(setColumnClass);
        }
      }
    }
  }
  isFilterRowCell($cell) {
    return false;
  }
  getKeyboardNavigationController() {
    return this._headersKeyboardNavigation;
  }
}
export const columnHeadersModule = {
  defaultOptions: () => ({
    showColumnHeaders: true,
    cellHintEnabled: true
  }),
  views: {
    columnHeadersView: ColumnHeadersView
  }
};